/**
 * utils/paymentAllocation.ts
 * The pure part of "Record payment": which documents a payment can settle, and
 * how an amount spreads over them.
 */

export interface OpenDoc {
  id: string;
  number: string;
  sub: string;
  balance: number;
  contactId?: string;
  currency?: string;
}

const OPEN_STATUSES = ['ISSUED', 'PARTIALLY_PAID', 'OVERDUE'];
const num = (v: unknown): number => {
  const n = parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
};

/**
 * The party's open documents with a balance, the current document FIRST, then
 * oldest first. Matched on the contact id — two customers can share a name.
 */
export function openDocsFor(rows: any[], contactId: string, currentId: string): OpenDoc[] {
  if (!contactId) return [];
  return rows
    .filter((d) => d && d.contact_id === contactId && OPEN_STATUSES.includes(d.status))
    .map((d) => ({
      id: d.id,
      number: d.doc_number || 'Draft',
      sub: d.doc_date || '',
      balance: num(d.grand_total) - num(d.amount_settled),
      contactId: d.contact_id,
      currency: d.currency,
    }))
    .filter((o) => o.balance > 0)
    .sort((a, b) => (a.id === currentId ? -1 : b.id === currentId ? 1 : a.sub.localeCompare(b.sub)));
}

/** Spread `avail` over the documents in order; each takes at most its balance. */
export function spreadAllocation(opens: OpenDoc[], avail: number): Record<string, string> {
  const next: Record<string, string> = {};
  let left = avail;
  opens.forEach((o) => {
    const take = Math.min(Math.max(left, 0), o.balance);
    next[o.id] = take > 0 ? take.toFixed(2) : '';
    left -= take;
  });
  return next;
}
